import { useEffect, useState } from 'react';
import { Button, Form, FormProps, Input } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

// services
import NotificationService from 'core/services/notification';
import { onGetAdmin, onUpdateAdmin } from 'modules/admins/services/admins';
import authService from 'core/services/auth';

//models
import { Admin } from 'modules/admins/models/Admin';

// components
import BackButton from 'components/backButton/backButton';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { useTranslation } from 'react-i18next';

type FieldType = {
	password: string;
	confirmPassword: string;
};

function AdminChangePasswordPage() {
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [admin, setAdmin] = useState<Admin>();
	const [changePasswordForm] = Form.useForm();
	const [confirmDirty, setConfirmDirty] = useState(false);
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	useEffect(() => {
		getAdmin();
	}, []);

	const getAdmin = async () => {
		try {
			setIsLoading(true);
			const adminResponse = await onGetAdmin(id as string);
			setAdmin(adminResponse.data.data);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			navigate('/admins');
		}
	};

	const validatePassword = (_: any, value: string) => {
		if (!value || changePasswordForm.getFieldValue('password') === value) {
			return Promise.resolve();
		}
		return Promise.reject(new Error(t('USERS.PASSWORDS_NOT_MATCH')));
	};

	const handleConfirmBlur = (e: any) => {
		const { value } = e.target;
		setConfirmDirty(confirmDirty || !!value);
	};

	const onDiscard = () => {
		navigate(-1);
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const updateAdminPayload = new FormData();
			updateAdminPayload.append('_method', 'put');
			updateAdminPayload.append('password', values.password);
			updateAdminPayload.append('password_confirmation', values.confirmPassword);
			await onUpdateAdmin(id as string, updateAdminPayload);

			setIsSubmitting(false);
			NotificationService.success(t('COMMON.SUCCESS'), t('ADMINS.PASSWORD_UPDATED'));
			navigate('/admins');
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('ADMINS.PASSWORD_NOT_UPDATED'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('USERS.CHANGE_PASSWORD')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<div className="sectionContainer">
						<h5 style={{ marginBottom: 15 }}>{admin?.name}</h5>
						<span className={`status ${admin?.active ? 'active' : 'inactive'}`}>
							{admin?.active ? t('COMMON.ACTIVE') : t('COMMON.INACTIVE')}
						</span>
					</div>

					<div className="sectionContainer">
						<h5 style={{ marginBottom: 15 }}>{t('USERS.CHANGE_PASSWORD')}</h5>

						<Form form={changePasswordForm} name="changePassword" labelAlign="left" onFinish={onSubmit} autoComplete="off">
							<Form.Item<FieldType>
								label={t('USERS.PASSWORD')}
								labelCol={{ span: 8 }}
								name="password"
								rules={[
									{ required: true, message: t('ADMINS.PASSWORD_REQUIRED') },
									{ min: 8, message: t('USERS.PASSWORD_8_CHARS') },
								]}>
								<Input.Password size="large" />
							</Form.Item>

							<Form.Item<FieldType>
								label={t('USERS.CONFIRM_PASSWORD')}
								labelCol={{ span: 8 }}
								name="confirmPassword"
								rules={[
									{ required: true, message: t('ADMINS.CONFIRM_PASSWORD_REQUIRED') },
									{
										validator: validatePassword,
									},
								]}>
								<Input.Password size="large" onBlur={handleConfirmBlur} />
							</Form.Item>

							<div className="d-flex justify-content-end align-items-center">
								<Button onClick={onDiscard} className="discard" style={{ marginInlineEnd: '20px' }}>
									{t('COMMON.DISCARD')}
								</Button>
								<Button type="primary" htmlType="submit" loading={isSubmitting}>
									{t('USERS.CHANGE_PASSWORD')}
								</Button>
							</div>
						</Form>
					</div>
				</>
			)}
		</>
	);
}

export default AdminChangePasswordPage;
